import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { WorkforceCardsLayout } from "@/components/rdo/WorkforceCardsLayout";
import { EquipmentCardsLayout } from "@/components/rdo/EquipmentCardsLayout";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  Cloud,
  Sun,
  Users,
  FileText,
  Timer,
  MessageSquare,
  Paperclip,
  Signature,
  Camera,
  Video
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface RDOData {
  // Header/Metadados
  obraName: string;
  client: string;
  address: string;
  responsible: string;
  reportNumber: string;
  date: string;
  weekDay: string;
  contractNumber: string;
  contractDeadline: string;
  elapsedTime: string;
  remainingTime: string;
  
  // Condições Climáticas
  weather: {
    morning: {
      condition: string;
      workable: boolean;
      observation?: string;
    };
    afternoon: {
      condition: string;
      workable: boolean;
      observation?: string;
    };
  };
  
  // Mão de Obra
  workforce: Array<{
    team: string;
    function: string;
    quantity: number;
    entry: string;
    exit: string;
    workedHours: number;
    extraHours: number;
    observations?: string;
  }>;
  
  // Atividades
  activities: Array<{
    activity: string;
    location: string;
    start: string;
    end: string;
    completion: number;
    description: string;
    responsible: string;
    linkedTask?: string;
    linkedTaskTitle?: string;
  }>;
  
  // Equipamentos
  equipment: Array<{
    type: string;
    identification: string;
    operator: string;
    entry: string;
    exit: string;
    workedHours: number;
    idleTime: number;
    condition: string;
  }>;
  
  // Ocorrências
  occurrences: Array<{
    type: string;
    description: string;
    impact: string;
    actionTaken: string;
    responsible: string;
    photos: string[];
  }>;
  
  // Materiais
  materials: {
    received: Array<{
      material: string;
      unit: string;
      quantity: number;
      document: string;
      supplier: string;
      time: string;
      observation?: string;
    }>;
    used: Array<{
      material: string;
      unit: string;
      quantity: number;
      location: string;
      observation?: string;
    }>;
  };
  
  // Comentários
  comments: Array<{
    user: {
      name: string;
      role: string;
      avatar?: string;
    };
    datetime: string;
    text: string;
    attachments: string[];
  }>;
  
  // Fotos
  photos: Array<{
    url: string;
    caption: string;
    timestamp: string;
  }>;
  
  // Vídeos
  videos: Array<{
    url: string;
    thumbnail: string;
    title: string;
    timestamp: string;
  }>;
  
  // Anexos
  attachments: Array<{
    name: string;
    type: string;
    size: string;
    url: string;
    timestamp: string;
  }>;
  
  // Assinaturas
  signatures: {
    workResponsible?: {
      name: string;
      document: string;
      role: string;
      timestamp: string;
      signature: string;
    };
    clientInspector?: {
      name: string;
      document: string;
      role: string;
      timestamp: string;
      signature: string;
    };
  };
  
  status: "draft" | "in_review" | "signed";
}

// Mock data
const mockRDOData: RDOData = {
  obraName: "Edifício Residencial Vista Verde",
  client: "Incorporadora ABC Ltda.",
  address: "Rua das Palmeiras, 123 - São Paulo/SP",
  responsible: "João Silva",
  reportNumber: "RDO-2024-001",
  date: "2024-01-15",
  weekDay: "Segunda-feira",
  contractNumber: "CT-2023-456",
  contractDeadline: "2024-12-31",
  elapsedTime: "15 dias",
  remainingTime: "350 dias",
  
  weather: {
    morning: {
      condition: "Ensolarado",
      workable: true,
      observation: "Condições ideais para trabalho"
    },
    afternoon: {
      condition: "Parcialmente nublado",
      workable: true
    }
  },
  
  workforce: [
    {
      team: "Equipe A - Estrutura",
      function: "Pedreiro",
      quantity: 5,
      entry: "07:00",
      exit: "16:00",
      workedHours: 8,
      extraHours: 1,
      observations: "1 hora extra para conclusão da laje"
    }
  ],
  
  activities: [
    {
      activity: "Concretagem da laje do 3º andar",
      location: "Bloco A - 3º andar",
      start: "08:00",
      end: "14:00",
      completion: 100,
      description: "Concretagem realizada com sucesso. Volume de 15m³ de concreto usinado.",
      responsible: "João Silva"
    }
  ],
  
  equipment: [
    {
      type: "Betoneira",
      identification: "BT-001",
      operator: "Pedro Santos",
      entry: "07:30",
      exit: "15:30",
      workedHours: 8,
      idleTime: 0,
      condition: "Bom"
    }
  ],
  
  occurrences: [
    {
      type: "Segurança",
      description: "Trabalhador sem EPI completo",
      impact: "Baixo",
      actionTaken: "Orientação e fornecimento de EPI adequado",
      responsible: "Técnico de Segurança",
      photos: []
    }
  ],
  
  materials: {
    received: [
      {
        material: "Concreto Usinado FCK 25",
        unit: "m³",
        quantity: 15,
        document: "NF 12345",
        supplier: "Concreteira XYZ",
        time: "07:30",
        observation: "Material conforme especificação"
      }
    ],
    used: [
      {
        material: "Cimento CP-II 50kg",
        unit: "sc",
        quantity: 20,
        location: "Bloco A - 3º andar",
        observation: "Utilizado na concretagem"
      }
    ]
  },
  
  comments: [
    {
      user: {
        name: "João Silva",
        role: "Engenheiro Responsável",
        avatar: "/avatars/joao.jpg"
      },
      datetime: "2024-01-15T14:30:00",
      text: "Concretagem realizada com sucesso. Equipe demonstrou boa produtividade.",
      attachments: []
    }
  ],
  
  photos: [
    {
      url: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400",
      caption: "Concretagem da laje - Início dos trabalhos",
      timestamp: "2024-01-15T08:00:00"
    }
  ],
  
  videos: [],
  attachments: [],
  signatures: {},
  status: "draft"
};

export default function RDOPublicPage() {
  const { id } = useParams<{ id: string }>();
  const [rdoData, setRdoData] = useState<RDOData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRDOData = () => {
      if (!id) {
        setError("ID do RDO não fornecido");
        setLoading(false);
        return;
      }

      try {
        // For now, we'll use mock data since the table doesn't exist yet
        // TODO: Implement actual RDO data fetching from Supabase after migration
        setRdoData(mockRDOData);
      } catch (err) {
        setError("Erro ao carregar RDO");
      } finally {
        setLoading(false);
      }
    };

    fetchRDOData();
  }, [id]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "signed":
        return <Badge className="bg-green-100 text-green-800">Assinado</Badge>;
      case "draft":
        return <Badge className="bg-yellow-100 text-yellow-800">Rascunho</Badge>;
      case "in_review":
        return <Badge className="bg-blue-100 text-blue-800">Em Revisão</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg">Carregando RDO...</p>
        </div>
      </div>
    );
  }

  if (error || !rdoData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">RDO não encontrado</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  const workforceTotals = {
    totalQuantity: rdoData.workforce.reduce((sum, item) => sum + item.quantity, 0),
    totalWorkedHours: rdoData.workforce.reduce((sum, item) => sum + item.workedHours, 0),
    totalExtraHours: rdoData.workforce.reduce((sum, item) => sum + item.extraHours, 0)
  };

  return (
    <div className="min-h-screen bg-background px-2 py-2">
      <div className="max-w-5xl mx-auto space-y-1">
        
        {/* Header Principal */}
        <Card>
          <CardHeader className="pt-1 pb-0">
            <div className="flex items-center justify-center">
              <div className="flex items-center gap-2">
                <img 
                  src="https://via.placeholder.com/120x40/0ea5e9/ffffff?text=CONSTRUTORA" 
                  alt="Logo da Construtora" 
                  className="h-8"
                />
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="py-1">
            <div className="flex gap-6">
              {/* Metadados da Obra - Lado Esquerdo */}
              <div className="flex-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Nome da Obra</label>
                    <p className="text-xs font-medium">{rdoData.obraName}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Cliente</label>
                    <p className="text-xs">{rdoData.client}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Endereço</label>
                    <p className="text-xs">{rdoData.address}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Responsável</label>
                    <p className="text-xs">{rdoData.responsible}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-muted-foreground">Status</label>
                    {getStatusBadge(rdoData.status)}
                  </div>
                </div>
              </div>

              {/* Tabela de Informações Contratuais - Lado Direito */}
              <div className="w-72">
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium text-xs py-0.5 px-1.5 bg-muted">Nº do Contrato</TableCell>
                      <TableCell className="text-xs py-0.5 px-1.5">{rdoData.contractNumber}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium text-xs py-0.5 px-1.5 bg-muted">Nº Relatório</TableCell>
                      <TableCell className="text-xs py-0.5 px-1.5">{rdoData.reportNumber}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium text-xs py-0.5 px-1.5 bg-muted">Data</TableCell>
                      <TableCell className="text-xs py-0.5 px-1.5">{format(new Date(rdoData.date), "dd/MM/yyyy", { locale: ptBR })}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium text-xs py-0.5 px-1.5 bg-muted">Dia da Semana</TableCell>
                      <TableCell className="text-xs py-0.5 px-1.5">{rdoData.weekDay}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium text-xs py-0.5 px-1.5 bg-muted">Prazo contratual</TableCell>
                      <TableCell className="text-xs py-0.5 px-1.5">{rdoData.contractDeadline}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium text-xs py-0.5 px-1.5 bg-muted">Prazo decorrido</TableCell>
                      <TableCell className="text-xs py-0.5 px-1.5 text-orange-600">{rdoData.elapsedTime}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium text-xs py-0.5 px-1.5 bg-muted">Prazo a vencer</TableCell>
                      <TableCell className="text-xs py-0.5 px-1.5 text-green-600">{rdoData.remainingTime}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Condições Climáticas */}
        <Card>
          <CardHeader className="pt-1 pb-0">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Cloud className="w-4 h-4" />
              Condições Climáticas
            </CardTitle>
          </CardHeader>
          <CardContent className="py-1">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2 text-xs">
                  <Sun className="w-3 h-3 text-yellow-500" />
                  Manhã
                </h4>
                <div className="space-y-1 text-xs">
                  <div><span className="font-medium">Tempo:</span> {rdoData.weather.morning.condition}</div>
                  <div><span className="font-medium">Condição:</span> 
                    <Badge variant={rdoData.weather.morning.workable ? "default" : "destructive"} className="ml-1 text-xs">
                      {rdoData.weather.morning.workable ? "Praticável" : "Impraticável"}
                    </Badge>
                  </div>
                  {rdoData.weather.morning.observation && (
                    <div><span className="font-medium">Observação:</span> {rdoData.weather.morning.observation}</div>
                  )}
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2 text-xs">
                  <Cloud className="w-3 h-3 text-blue-500" />
                  Tarde
                </h4>
                <div className="space-y-1 text-xs">
                  <div><span className="font-medium">Tempo:</span> {rdoData.weather.afternoon.condition}</div>
                  <div><span className="font-medium">Condição:</span>
                    <Badge variant={rdoData.weather.afternoon.workable ? "default" : "destructive"} className="ml-1 text-xs">
                      {rdoData.weather.afternoon.workable ? "Praticável" : "Impraticável"}
                    </Badge>
                  </div>
                  {rdoData.weather.afternoon.observation && (
                    <div><span className="font-medium">Observação:</span> {rdoData.weather.afternoon.observation}</div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mão de Obra */}
        <Card>
          <CardHeader className="pt-1 pb-0">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Users className="w-4 h-4" />
              Mão de Obra
            </CardTitle>
          </CardHeader>
          <CardContent className="py-1">
            <WorkforceCardsLayout workforce={rdoData.workforce} showTotals={true} />
          </CardContent>
        </Card>

        {/* Atividades */}
        <Card>
          <CardHeader className="pt-1 pb-0">
            <CardTitle className="flex items-center gap-2 text-sm">
              <FileText className="w-4 h-4" />
              Atividades
            </CardTitle>
          </CardHeader>
          <CardContent className="py-1">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Atividade</TableHead>
                  <TableHead className="text-xs">Localização</TableHead>
                  <TableHead className="text-xs">Início</TableHead>
                  <TableHead className="text-xs">Fim</TableHead>
                  <TableHead className="text-xs">Conclusão</TableHead>
                  <TableHead className="text-xs">Descrição</TableHead>
                  <TableHead className="text-xs">Responsável</TableHead>
                  <TableHead className="text-xs">Tarefa Vinculada</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rdoData.activities.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell className="text-xs">{item.activity}</TableCell>
                    <TableCell className="text-xs">{item.location}</TableCell>
                    <TableCell className="text-xs">{item.start}</TableCell>
                    <TableCell className="text-xs">{item.end}</TableCell>
                    <TableCell className="text-xs">{item.completion}</TableCell>
                    <TableCell className="text-xs">{item.description}</TableCell>
                    <TableCell className="text-xs">{item.responsible}</TableCell>
                    <TableCell className="text-xs">{item.linkedTaskTitle || ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Equipamentos */}
        <Card>
          <CardHeader className="pt-1 pb-0">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Timer className="w-4 h-4" />
              Equipamentos
            </CardTitle>
          </CardHeader>
          <CardContent className="py-1">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Tipo</TableHead>
                  <TableHead className="text-xs">Identificação</TableHead>
                  <TableHead className="text-xs">Operador</TableHead>
                  <TableHead className="text-xs">Entrada</TableHead>
                  <TableHead className="text-xs">Saída</TableHead>
                  <TableHead className="text-xs">H.Trab</TableHead>
                  <TableHead className="text-xs">Tempo Ocioso</TableHead>
                  <TableHead className="text-xs">Condição</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rdoData.equipment.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell className="text-xs">{item.type}</TableCell>
                    <TableCell className="text-xs">{item.identification}</TableCell>
                    <TableCell className="text-xs">{item.operator}</TableCell>
                    <TableCell className="text-xs">{item.entry}</TableCell>
                    <TableCell className="text-xs">{item.exit}</TableCell>
                    <TableCell className="text-xs">{item.workedHours}</TableCell>
                    <TableCell className="text-xs">{item.idleTime}</TableCell>
                    <TableCell className="text-xs">{item.condition}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Ocorrências */}
        <Card>
          <CardHeader className="pt-1 pb-0">
            <CardTitle className="flex items-center gap-2 text-sm">
              <MessageSquare className="w-4 h-4" />
              Ocorrências
            </CardTitle>
          </CardHeader>
          <CardContent className="py-1">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Tipo</TableHead>
                  <TableHead className="text-xs">Descrição</TableHead>
                  <TableHead className="text-xs">Impacto</TableHead>
                  <TableHead className="text-xs">Ação Tomada</TableHead>
                  <TableHead className="text-xs">Responsável</TableHead>
                  <TableHead className="text-xs">Fotos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rdoData.occurrences.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell className="text-xs">{item.type}</TableCell>
                    <TableCell className="text-xs">{item.description}</TableCell>
                    <TableCell className="text-xs">{item.impact}</TableCell>
                    <TableCell className="text-xs">{item.actionTaken}</TableCell>
                    <TableCell className="text-xs">{item.responsible}</TableCell>
                    <TableCell className="text-xs">
                      {item.photos.map((photo, photoIndex) => (
                        <a key={photoIndex} href={photo} target="_blank" rel="noopener noreferrer" className="underline text-blue-500">
                          Foto {photoIndex + 1}
                        </a>
                      ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Materiais */}
        <Card>
          <CardHeader className="pt-1 pb-0">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Paperclip className="w-4 h-4" />
              Materiais
            </CardTitle>
          </CardHeader>
          <CardContent className="py-1">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold mb-2 text-xs">Recebidos</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Material</TableHead>
                      <TableHead className="text-xs">Unidade</TableHead>
                      <TableHead className="text-xs">Qtd.</TableHead>
                      <TableHead className="text-xs">Documento</TableHead>
                      <TableHead className="text-xs">Fornecedor</TableHead>
                      <TableHead className="text-xs">Hora</TableHead>
                      <TableHead className="text-xs">Observação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rdoData.materials.received.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="text-xs">{item.material}</TableCell>
                        <TableCell className="text-xs">{item.unit}</TableCell>
                        <TableCell className="text-xs">{item.quantity}</TableCell>
                        <TableCell className="text-xs">{item.document}</TableCell>
                        <TableCell className="text-xs">{item.supplier}</TableCell>
                        <TableCell className="text-xs">{item.time}</TableCell>
                        <TableCell className="text-xs">{item.observation}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div>
                <h4 className="font-semibold mb-2 text-xs">Utilizados</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Material</TableHead>
                      <TableHead className="text-xs">Unidade</TableHead>
                      <TableHead className="text-xs">Qtd.</TableHead>
                      <TableHead className="text-xs">Localização</TableHead>
                      <TableHead className="text-xs">Observação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rdoData.materials.used.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="text-xs">{item.material}</TableCell>
                        <TableCell className="text-xs">{item.unit}</TableCell>
                        <TableCell className="text-xs">{item.quantity}</TableCell>
                        <TableCell className="text-xs">{item.location}</TableCell>
                        <TableCell className="text-xs">{item.observation}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Comentários */}
        <Card>
          <CardHeader className="pt-1 pb-0">
            <CardTitle className="flex items-center gap-2 text-sm">
              <MessageSquare className="w-4 h-4" />
              Comentários
            </CardTitle>
          </CardHeader>
          <CardContent className="py-1">
            {rdoData.comments.map((comment, index) => (
              <div key={index} className="mb-4">
                <div className="flex items-start gap-2">
                  <Avatar className="w-6 h-6">
                    <AvatarImage src={comment.user.avatar} />
                    <AvatarFallback>{comment.user.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="text-xs font-semibold">{comment.user.name}</div>
                    <div className="text-xs text-muted-foreground">{comment.user.role} - {format(new Date(comment.datetime), "dd/MM/yyyy HH:mm", { locale: ptBR })}</div>
                  </div>
                </div>
                <div className="mt-2 text-sm">{comment.text}</div>
                {comment.attachments.length > 0 && (
                  <div className="mt-2">
                    Anexos:
                    {comment.attachments.map((attachment, attachmentIndex) => (
                      <a key={attachmentIndex} href={attachment} target="_blank" rel="noopener noreferrer" className="block text-blue-500 underline text-xs">
                        Anexo {attachmentIndex + 1}
                      </a>
                    ))}
                  </div>
                )}
                <Separator className="my-2" />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Fotos */}
        <Card>
          <CardHeader className="pt-1 pb-0">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Camera className="w-4 h-4" />
              Fotos
            </CardTitle>
          </CardHeader>
          <CardContent className="py-1">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {rdoData.photos.map((photo, index) => (
                <div key={index} className="relative">
                  <img src={photo.url} alt={photo.caption} className="w-full rounded-md aspect-square object-cover" />
                  <div className="absolute bottom-0 left-0 bg-background/80 text-foreground p-2 text-xs rounded-br-md rounded-bl-md">
                    {photo.caption} - {format(new Date(photo.timestamp), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Vídeos */}
        <Card>
          <CardHeader className="pt-1 pb-0">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Video className="w-4 h-4" />
              Vídeos
            </CardTitle>
          </CardHeader>
          <CardContent className="py-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {rdoData.videos.map((video, index) => (
                <div key={index} className="relative">
                  <img src={video.thumbnail} alt={video.title} className="w-full rounded-md aspect-video object-cover" />
                  <div className="absolute bottom-0 left-0 bg-background/80 text-foreground p-2 text-xs rounded-br-md rounded-bl-md">
                    {video.title} - {format(new Date(video.timestamp), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Anexos */}
        <Card>
          <CardHeader className="pt-1 pb-0">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Paperclip className="w-4 h-4" />
              Anexos
            </CardTitle>
          </CardHeader>
          <CardContent className="py-1">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Nome</TableHead>
                  <TableHead className="text-xs">Tipo</TableHead>
                  <TableHead className="text-xs">Tamanho</TableHead>
                  <TableHead className="text-xs">URL</TableHead>
                  <TableHead className="text-xs">Data/Hora</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rdoData.attachments.map((attachment, index) => (
                  <TableRow key={index}>
                    <TableCell className="text-xs">{attachment.name}</TableCell>
                    <TableCell className="text-xs">{attachment.type}</TableCell>
                    <TableCell className="text-xs">{attachment.size}</TableCell>
                    <TableCell className="text-xs">
                      <a href={attachment.url} target="_blank" rel="noopener noreferrer" className="underline text-blue-500">
                        Download
                      </a>
                    </TableCell>
                    <TableCell className="text-xs">{format(new Date(attachment.timestamp), "dd/MM/yyyy HH:mm", { locale: ptBR })}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Assinaturas */}
        <Card>
          <CardHeader className="pt-1 pb-0">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Signature className="w-4 h-4" />
              Assinaturas
            </CardTitle>
          </CardHeader>
          <CardContent className="py-1">
            <div className="grid md:grid-cols-2 gap-4">
              {rdoData.signatures.workResponsible && (
                <div>
                  <h4 className="font-semibold mb-2 text-xs">Responsável pela Obra</h4>
                  <div className="space-y-1 text-xs">
                    <div><span className="font-medium">Nome:</span> {rdoData.signatures.workResponsible.name}</div>
                    <div><span className="font-medium">Documento:</span> {rdoData.signatures.workResponsible.document}</div>
                    <div><span className="font-medium">Cargo:</span> {rdoData.signatures.workResponsible.role}</div>
                    <div><span className="font-medium">Data/Hora:</span> {format(new Date(rdoData.signatures.workResponsible.timestamp), "dd/MM/yyyy HH:mm", { locale: ptBR })}</div>
                    <div><span className="font-medium">Assinatura:</span> <a href={rdoData.signatures.workResponsible.signature} target="_blank" rel="noopener noreferrer" className="underline text-blue-500">Ver Assinatura</a></div>
                  </div>
                </div>
              )}
              {rdoData.signatures.clientInspector && (
                <div>
                  <h4 className="font-semibold mb-2 text-xs">Fiscal do Cliente</h4>
                  <div className="space-y-1 text-xs">
                    <div><span className="font-medium">Nome:</span> {rdoData.signatures.clientInspector.name}</div>
                    <div><span className="font-medium">Documento:</span> {rdoData.signatures.clientInspector.document}</div>
                    <div><span className="font-medium">Cargo:</span> {rdoData.signatures.clientInspector.role}</div>
                    <div><span className="font-medium">Data/Hora:</span> {format(new Date(rdoData.signatures.clientInspector.timestamp), "dd/MM/yyyy HH:mm", { locale: ptBR })}</div>
                    <div><span className="font-medium">Assinatura:</span> <a href={rdoData.signatures.clientInspector.signature} target="_blank" rel="noopener noreferrer" className="underline text-blue-500">Ver Assinatura</a></div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
